'use client'

import { useEffect } from 'react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { useNoticeDetailQuery } from '@/hooks/useContentfulQuery'
import { createNoticeRichTextRenderOptions } from '@/utils/renderOption/noticeRenderOption'
import { GA_EVENTS } from '@/utils/analytics'
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton'
import type { Document } from '@contentful/rich-text-types'

type NoticeDetailProps = {
  id?: string
  isModal?: boolean
}

const NoticeDetail = ({ id: idProp, isModal = false }: NoticeDetailProps) => {
  const { t } = useTranslation('notice')
  const searchParams = useSearchParams()
  const id = idProp ?? searchParams.get('id') ?? ''

  const { data, isLoading, isError } = useNoticeDetailQuery(id)
  const notice = data?.notice
  const assets = data?.assets ?? []

  useEffect(() => {
    if (!notice) return
    GA_EVENTS.NOTICE_VIEW(notice.content)
  }, [notice])

  if (isLoading) return null
  if (isError || !notice) return <div>Notice not found</div>

  const processedDate = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(notice.authoredAt))

  const renderOptions = createNoticeRichTextRenderOptions(assets)

  return (
    <section className={isModal ? '' : 'sm:mt-5 pb-[100px]'}>
      {!isModal && (
        <div className="sm:hidden">
          <HeaderWithBackButton />
        </div>
      )}
      <div className="mx-auto flex flex-col gap-3 px-5 max-w-lg">
        <div className="flex flex-col gap-3">
          <p className="py-1 px-2 mr-auto text-[11px] font-pretendard-semibold text-white dark:text-black bg-main-light-2 dark:bg-main-dark-2 rounded-md">
            {t(notice.category)}
          </p>
          <h2 className="font-pretendard-semibold text-[19px] text-main-light-2 dark:text-main-dark-2 leading-tight">
            {notice.content}
          </h2>
          {notice.thumbnail ? (
            <Image
              src={notice.thumbnail}
              alt="notice"
              width={327}
              height={188}
              className="w-full rounded-lg"
            />
          ) : null}
          <div className="flex flex-col gap-[10px]">
            {notice.body ? documentToReactComponents(notice.body as Document, renderOptions) : null}
          </div>
          <p className="font-pretendard-regular text-[13px] text-main-light-4 dark:text-main-dark-4">
            {processedDate}
          </p>
        </div>
      </div>
    </section>
  )
}

export default NoticeDetail


